import { LayoutModule } from '@angular/cdk/layout';
import { PortalModule } from '@angular/cdk/portal';
import { CdkTreeModule } from '@angular/cdk/tree';
import { CommonModule, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { BrowserModule, MaintenanceService } from '@campus/browser';
import { ComponentsModule } from '@campus/components';
import { FroalaModule } from '@campus/external-froala';
import { SearchModule } from '@campus/search';
import { UiModule } from '@campus/ui';
import { FILTER_SERVICE_TOKEN, FilterService, MapObjectConversionService, UtilsModule } from '@campus/utils';
import '@diekeure/dcr-components/dcr-divider.js';
import { Store } from '@ngrx/store';
import { ImageCropperComponent as ImageCropper } from 'ngx-image-cropper';
import { HasPermissionDirective } from './auth/has-permission.directive';
import { PermissionService } from './auth/permission.service';
import {
  BooksTileComponent,
  ImageCropperComponent as CampusImageCropper,
  ContentPreviewDialogComponent,
  ErrorDialogComponent,
  EvaluationMethodSlideoutComponent,
  GlobalAssigneeFilterComponent,
  LearningPathComponent,
  LearningPlanGoalProgressManagementComponent,
  NewsModalComponent,
  QuickLinkComponent,
  ResultGuideComponent,
  ScoreComponent,
  TocTriggerComponent,
  ToolbarActionsDirective,
  ToolbarComponent,
  ToolbarContainerDirective,
  ToolbarFabDirective,
  ToolbarListHeaderDirective,
  ToolbarNavigationDirective,
} from './components';
import { ChapterLessonSelectComponent } from './components/chapter-lesson-select';
import { CheckboxTreeComponent } from './components/checkbox-tree/checkbox-tree.component';
import { EduContentDisplaySectionComponent } from './components/edu-content-display-section/edu-content-display-section.component';
import { EduContentListSectionComponent } from './components/edu-content-list-section/edu-content-list-section.component';
import { EduContentResultLoaderComponent } from './components/edu-content-result-loader/edu-content-result-loader.component';
import { EvaluationStartedModalComponent } from './components/evaluation-started-modal/evaluation-started-modal.component';
import { EvaluationSubjectsSlideoutComponent } from './components/evaluations-subjects-slideout/evaluations-subjects-slideout.component';
import { FileUploadDropZoneComponent } from './components/file-upload-drop-zone/file-upload-drop-zone.component';
import { FileUploadZoneComponent } from './components/file-upload-zone';
import { HeaderComponent } from './components/header/header.component';
import { PageBarContainerComponent } from './components/page-bar-container/page-bar-container.component';
import { AbilityProgressGuideComponent } from './components/result-guide/ability-progress-guide.component';
import { ShareToComponent } from './components/share-to/share-to.component';
import { TaskCardComponent } from './components/task-card/task-card.component';
import { TocSelectComponent } from './components/toc-select';
import { CampusUIntTextFieldDirective, ClickOutsideDirective } from './directives';
import { CampusRouterlinkDirective } from './directives/campus-routerlink.directive';
import { ContentOpenerFactory } from './factories';
import { CampusHttpInterceptor } from './interceptors/campus-http.interceptor';
import { AssetsPathPipe, MathJaxPipe, MethodsImagesPathPipe } from './pipes';
import { AlertToNotificationItemPipe } from './pipes/alert-to-notification/alert-to-notification-pipe';
import { EuroPipe } from './pipes/euro/euro-pipe';
import { MailToByCredentialPipe } from './pipes/mail-to/mail-to-credential-pipe';
import { MapPipe } from './pipes/map/map.pipe';
import { IsNumberPipe } from './pipes/number/is-number.pipe';
import { TextMappingPipe } from './pipes/text-mapping/text-mapping.pipe';
import {
  BOOKS_TILE_SERVICE_TOKEN,
  CONTENT_OPENER_TOKEN,
  CONTENT_OPEN_ACTIONS_SERVICE_TOKEN,
  ContentOpenActionsStudentService,
  STUDENT_TASK_SERVICE_TOKEN,
  ShareToService,
  StudentTaskService,
  booksTileServiceDeps,
  booksTileServiceFactory,
} from './services';
import { EduContentCollectionManagerService } from './services/collection-manager/edu-content-collection-manager.service';
import { EDU_CONTENT_COLLECTION_MANAGER_SERVICE_TOKEN } from './services/collection-manager/edu-content-collection-manager.service.interface';
import { DefaultContentOpenerService } from './services/content-actions/content-open-actions/default-content-opener.service';
import { OPEN_STATIC_CONTENT_SERVICE_TOKEN } from './services/content/open-static-content.interface';
import { OpenStaticContentService } from './services/content/open-static-content.service';
import { FEEDBACK_SERVICE_TOKEN, FeedBackService } from './services/feedback';
import { SNACKBAR_DEFAULT_CONFIG_TOKEN, SnackBarDefaultConfig } from './services/feedback/snackbar.config';
import { GameLearningEventsService } from './services/game-learning-events/game-learning-events.service';
import { GoogleAnalyticsService } from './services/google-analytics/google-analytics.service';
import { GOOGLE_ANALYTICS_SERVICE_TOKEN } from './services/google-analytics/google-analytics.service.interface';
import { MapperStudentStrandsService } from './services/practice/mapper.student-strands.service';
import { MAPPER_STUDENT_STRANDS_SERVICE_TOKEN } from './services/practice/mapper.student-strands.service.interface';
import { MapperStudentTaskService } from './services/task/mapper.student-task.service';
import { MAPPER_STUDENT_TASK_SERVICE_TOKEN } from './services/task/mapper.student-task.service.interface';

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    UiModule,
    PortalModule,
    LayoutModule,
    MatBadgeModule,
    RouterModule,
    MatSnackBarModule,
    MatDialogModule,
    MatSelectModule,
    MatButtonModule,
    MatListModule,
    MatTooltipModule,
    UtilsModule,
    MatMenuModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    HttpClientModule,
    MatInputModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    FormsModule,
    SearchModule,
    ComponentsModule,
    BrowserModule,
    CdkTreeModule,
    FroalaModule,
    ImageCropper,
    ComponentsModule,
    MathJaxPipe,
  ],
  declarations: [
    HeaderComponent,
    PageBarContainerComponent,
    LearningPathComponent,
    HasPermissionDirective,
    ClickOutsideDirective,
    MailToByCredentialPipe,
    CampusRouterlinkDirective,
    AlertToNotificationItemPipe,
    QuickLinkComponent,
    ContentPreviewDialogComponent,
    LearningPlanGoalProgressManagementComponent,
    ChapterLessonSelectComponent,
    AssetsPathPipe,
    TextMappingPipe,
    MethodsImagesPathPipe,
    FileUploadDropZoneComponent,
    EuroPipe,
    TaskCardComponent,
    TocSelectComponent,
    ToolbarComponent,
    ToolbarFabDirective,
    ToolbarActionsDirective,
    ToolbarNavigationDirective,
    ToolbarContainerDirective,
    ToolbarListHeaderDirective,
    BooksTileComponent,
    ScoreComponent,
    ShareToComponent,
    ResultGuideComponent,
    AbilityProgressGuideComponent,
    EvaluationSubjectsSlideoutComponent,
    EvaluationStartedModalComponent,
    MapPipe,
    EvaluationMethodSlideoutComponent,
    IsNumberPipe,
    CheckboxTreeComponent,
    EduContentResultLoaderComponent,
    CampusUIntTextFieldDirective,
    CampusImageCropper,
    EduContentDisplaySectionComponent,
    EduContentListSectionComponent,
    FileUploadZoneComponent,
    TocTriggerComponent,
    ErrorDialogComponent,
    NewsModalComponent,
    GlobalAssigneeFilterComponent,
  ],
  exports: [
    TocSelectComponent,
    HeaderComponent,
    PortalModule,
    LayoutModule,
    PageBarContainerComponent,
    HasPermissionDirective,
    MailToByCredentialPipe,
    CampusRouterlinkDirective,
    ClickOutsideDirective,
    AlertToNotificationItemPipe,
    QuickLinkComponent,
    ContentPreviewDialogComponent,
    ChapterLessonSelectComponent,
    AssetsPathPipe,
    TextMappingPipe,
    MethodsImagesPathPipe,
    FileUploadDropZoneComponent,
    EuroPipe,
    LearningPathComponent,
    UtilsModule,
    TaskCardComponent,
    ToolbarComponent,
    ToolbarFabDirective,
    ToolbarActionsDirective,
    ToolbarNavigationDirective,
    ToolbarContainerDirective,
    ToolbarListHeaderDirective,
    BooksTileComponent,
    ScoreComponent,
    ShareToComponent,
    ResultGuideComponent,
    AbilityProgressGuideComponent,
    EvaluationSubjectsSlideoutComponent,
    EvaluationStartedModalComponent,
    MapPipe,
    EvaluationMethodSlideoutComponent,
    MathJaxPipe,
    IsNumberPipe,
    CheckboxTreeComponent,
    CampusImageCropper,
    EduContentResultLoaderComponent,
    CampusUIntTextFieldDirective,
    EduContentDisplaySectionComponent,
    EduContentListSectionComponent,
    FileUploadZoneComponent,
    TocTriggerComponent,
    ErrorDialogComponent,
    NewsModalComponent,
    GlobalAssigneeFilterComponent,
  ],
  providers: [
    AssetsPathPipe,
    TextMappingPipe,
    MapObjectConversionService,
    ShareToService,
    MaintenanceService,
    GameLearningEventsService,
    { provide: FILTER_SERVICE_TOKEN, useClass: FilterService },
    PermissionService,
    DefaultContentOpenerService,
    ContentOpenActionsStudentService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CampusHttpInterceptor,
      multi: true,
    },
    {
      provide: OPEN_STATIC_CONTENT_SERVICE_TOKEN,
      useClass: OpenStaticContentService,
    },
    { provide: FEEDBACK_SERVICE_TOKEN, useClass: FeedBackService },
    {
      provide: SNACKBAR_DEFAULT_CONFIG_TOKEN,
      useClass: SnackBarDefaultConfig,
    },
    {
      provide: EDU_CONTENT_COLLECTION_MANAGER_SERVICE_TOKEN,
      useClass: EduContentCollectionManagerService,
    },
    {
      provide: CONTENT_OPEN_ACTIONS_SERVICE_TOKEN,
      useFactory: ContentOpenerFactory,
      deps: [Store, CONTENT_OPENER_TOKEN, DefaultContentOpenerService],
    },
    AlertToNotificationItemPipe,
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    {
      provide: MAPPER_STUDENT_STRANDS_SERVICE_TOKEN,
      useClass: MapperStudentStrandsService,
    },
    {
      provide: MAPPER_STUDENT_TASK_SERVICE_TOKEN,
      useClass: MapperStudentTaskService,
    },
    { provide: STUDENT_TASK_SERVICE_TOKEN, useClass: StudentTaskService },
    {
      provide: GOOGLE_ANALYTICS_SERVICE_TOKEN,
      useClass: GoogleAnalyticsService,
    },
    {
      provide: BOOKS_TILE_SERVICE_TOKEN,
      useFactory: booksTileServiceFactory,
      deps: booksTileServiceDeps,
    },
  ],
})
export class SharedModule {}
